// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
//
// SVG edge renderer for the phylogenetic tree viewer.
//
// renderEdges(graph, props) → SVG <g> element containing all branch edges
// for the tree, handling all five TreeTypes (Circular, Diagonal, Hierarchical,
// Radial, Rectangular).

import { el, rgba } from "./dom.js";
import { TreeTypes } from "../core/constants.js";

// ---------------------------------------------------------------------------
// Arc helpers
// ---------------------------------------------------------------------------

function polarToCartesian(centerX, centerY, radius, angleInRadians) {
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

/**
 * Build an SVG arc path-data string going counter-clockwise from startAngle
 * to endAngle at the given radius around (x, y).
 *
 * @param {number} x
 * @param {number} y
 * @param {number} radius
 * @param {number} startAngle - radians
 * @param {number} endAngle   - radians
 * @returns {string}
 */
function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";
  const d = [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
  ];
  return d.join(" ");
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render all branch edges of the phylogenetic tree as SVG elements.
 *
 * @param {Object} graph  - Graph object returned by buildGraph().
 * @param {Object} props  - Resolved props (includes type, strokeColour, strokeWidth).
 * @returns {SVGGElement} - An SVG <g> grouping all edge elements.
 */
export function renderEdges(graph, props) {
  const type = props.type;

  const strokeColour = Array.isArray(props.strokeColour)
    ? rgba(props.strokeColour)
    : (props.strokeColour || "rgb(34,34,34)");

  const strokeWidth = props.strokeWidth != null ? props.strokeWidth : 1;

  // All edges share these inherited presentation attributes on the group so
  // individual elements do not need to repeat them.
  const group = el("g", {
    stroke: strokeColour,
    "stroke-width": strokeWidth,
    fill: "none",
    "stroke-linejoin": "miter",
  });

  for (let i = graph.firstIndex + 1; i < graph.lastIndex; i++) {
    const node = graph.preorderTraversal[i];

    // Defensive guard: root should never appear here, but skip if no parent.
    if (!node.parent) continue;

    if (type === TreeTypes.Circular) {
      // Radial branch line from node to its arc-connection point.
      const radialLine = el("line", {
        x1: node.x,
        y1: node.y,
        x2: node.cx,
        y2: node.cy,
        "data-node-id": node.id,
      });
      group.appendChild(radialLine);

      // Arc connecting first and last child around the parent's distance ring.
      if (node.children && node.children.length > 0 && !node.isCollapsed) {
        const firstChild = node.children[0];
        const lastChild = node.children[node.children.length - 1];
        const arc = el("path", {
          fill: "none",
          "stroke-linecap": "round",
          d: describeArc(
            graph.root.x,
            graph.root.y,
            node.dist,
            firstChild.angle,
            lastChild.angle
          ),
        });
        group.appendChild(arc);
      }
    } else if (type === TreeTypes.Diagonal || type === TreeTypes.Radial) {
      // Single straight line directly to parent.
      const line = el("line", {
        x1: node.x,
        y1: node.y,
        x2: node.parent.x,
        y2: node.parent.y,
        "data-node-id": node.id,
      });
      group.appendChild(line);
    } else if (type === TreeTypes.Hierarchical) {
      // One elbow (vertical then horizontal) so the corner joins cleanly (miter)
      // instead of leaving a notch between two butt-capped lines.
      const elbow = el("polyline", {
        points: [
          `${node.x},${node.y}`,
          `${node.x},${node.parent.y}`,
          `${node.parent.x},${node.parent.y}`,
        ].join(" "),
        "data-node-id": node.id,
      });
      group.appendChild(elbow);
    } else if (type === TreeTypes.Rectangular) {
      // One elbow (horizontal then vertical) so the outer corner joins cleanly
      // (miter) instead of leaving a notch between two butt-capped lines.
      const elbow = el("polyline", {
        points: [
          `${node.x},${node.y}`,
          `${node.parent.x},${node.y}`,
          `${node.parent.x},${node.parent.y}`,
        ].join(" "),
        "data-node-id": node.id,
      });
      group.appendChild(elbow);
    }

    // Skip the interior of collapsed subtrees; their edges are not drawn.
    if (node.isCollapsed) {
      i += node.totalNodes - 1;
    }
  }

  return group;
}
